import React, { useCallback, useEffect, useState } from 'react';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import {
  EmptyState,
  LoadingOverlay,
  PageHeader,
  PagePaper,
  Stars,
  StatusChip,
} from 'dashboard/components/common';

const STATUS_COLORS = { pending: 'warning', approved: 'success', rejected: 'error' };

/**
 * Owner moderation queue for product reviews. Approve / reject / delete
 * per-row; the public storefront only renders approved reviews.
 */
export default function ReviewsModeration() {
  const [selectedBrand] = useOutletContext();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!selectedBrand) return Promise.resolve();
    setLoading(true);
    return API.listProductReviews({
      brand_id: selectedBrand,
      status: statusFilter || undefined,
    })
      .then((res) => setItems(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load reviews', { toastId: 'reviews-load-error' }))
      .finally(() => setLoading(false));
  }, [selectedBrand, statusFilter]);

  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listProductReviews({
      brand_id: selectedBrand,
      status: statusFilter || undefined,
    })
      .then((res) => { if (!cancelled) setItems(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load reviews', { toastId: 'reviews-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand, statusFilter]);

  const moderate = (review, status) => {
    API.moderateProductReview({
      brand_id: selectedBrand,
      _id: review._id,
      status,
    })
      .then(() => { toast.success(status === 'approved' ? 'Approved' : status); load(); })
      .catch((err) => toast.error(err?.message || 'Failed to update'));
  };

  const remove = (review) => {
    if (!window.confirm('Delete this review?')) return;
    API.deleteProductReview({ brand_id: selectedBrand, _id: review._id })
      .then(() => { toast.success('Deleted'); load(); })
      .catch((err) => toast.error(err?.message || 'Failed to delete'));
  };

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <PageHeader
          title='Product reviews'
          subtitle='Approve customer reviews to display them on the product page.'
          actions={
            <FormControl size='small' sx={{ minWidth: 180 }}>
              <InputLabel shrink>Status</InputLabel>
              <Select
                displayEmpty
                label='Status'
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value='pending'>Pending</MenuItem>
                <MenuItem value='approved'>Approved</MenuItem>
                <MenuItem value='rejected'>Rejected</MenuItem>
                <MenuItem value=''>All</MenuItem>
              </Select>
            </FormControl>
          }
        />

        {!selectedBrand && <EmptyState>Select a brand to moderate its reviews.</EmptyState>}

        {selectedBrand && (
          <LoadingOverlay loading={loading && items.length === 0} minHeight={200}>
          <TableContainer
            component={Box}
            sx={{
              border: '1px solid', borderColor: 'divider',
              borderRadius: '12px', overflowX: 'auto',
            }}
          >
            <Table size='medium'>
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell>Review</TableCell>
                  <TableCell>Reviewer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && items.length === 0 && (
                  <TableRow><TableCell colSpan={4}><EmptyState>Loading…</EmptyState></TableCell></TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow><TableCell colSpan={4}><EmptyState>Nothing to moderate.</EmptyState></TableCell></TableRow>
                )}
                {items.map((r) => (
                  <TableRow key={r._id}>
                    <TableCell sx={{ maxWidth: 480 }}>
                      <Stars value={r.rating} />
                      {r.title && <Typography fontWeight='600' mt='4px'>{r.title}</Typography>}
                      {r.body && (
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          mt='2px'
                          sx={{
                            whiteSpace: 'pre-wrap',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {r.body}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{r.author_name || '—'}</Typography>
                      {r.verified_purchase && (
                        <Chip
                          size='small'
                          label='VERIFIED'
                          color='success'
                          sx={{ mt: '4px', height: 18, fontSize: '0.65em' }}
                        />
                      )}
                      <Typography variant='caption' color='text.secondary' display='block' mt='2px'>
                        {r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={r.status} colors={STATUS_COLORS} />
                    </TableCell>
                    <TableCell align='right'>
                      <Stack direction='row' spacing='4px' justifyContent='flex-end' flexWrap='wrap'>
                        {r.status !== 'approved' && (
                          <Tooltip title='Approve'>
                            <IconButton size='small' color='success' onClick={() => moderate(r, 'approved')}>
                              <CheckIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        )}
                        {r.status !== 'rejected' && (
                          <Tooltip title='Reject'>
                            <IconButton size='small' color='warning' onClick={() => moderate(r, 'rejected')}>
                              <CloseIcon fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title='Delete'>
                          <IconButton size='small' color='error' onClick={() => remove(r)}>
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </LoadingOverlay>
        )}
      </PagePaper>
    </Box>
  );
}
